import {
  faAward,
  faBars,
  faBell,
  faBuilding,
  faDatabase,
  faGlasses,
  faRestroom,
  faSpellCheck,
  faStickyNote,
  faTable,
  faThumbsUp,
  faUniversalAccess,
  faUserCheck,
  faUserLock,
  faUsers,
  faVideo,
  faTrophy,
  faShieldAlt,
  faClipboardList,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { FunctionComponent, useMemo } from "react";
import { Col, Nav, OverlayTrigger, Popover, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import { ApplicationState } from "../../../store";
import { UserAssignmentRole } from "../../../store/auth/types";
import { CoachDashboardTabKeys } from "../../pages/coach-dashboard/CoachDashboard";
import { DistrictOwnerDashboardTabKeys } from "../../pages/district-owner-dashboard/DistrictOwnerDashboard";
import CoachDataPeriodSelector from "../../pages/coach-dashboard/CoachDataPeriodSelector";
import DashboardMenuPanel from "../../pages/coach-dashboard/DashboardMenuPanel";
import GroupsInProgressNavLink from "../../pages/coach-dashboard/tabs/groups-in-progress/GroupsInProgressNavLink";
import DataPeriodsPanel from "../../pages/coach-dashboard/tabs/your-data-and-interviews/DataPeriodsPanel";
import useActiveTab from "../../pages/parent-dashboard/useActiveTab";
import useUserRole from "../../../utils/hooks/useUserRole";
import { EventKey } from "react-bootstrap/esm/types";
import { districtRoles } from "../../../store/auth/constants";
import { push } from "connected-react-router";
import { useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import HelpButton from "../page-help/HelpButton";
import SettingButton from "../permission-setting/SettingButton";
import { changeDisplayedBar } from "../../../store/help/actions";

type MatchParams = {
  tabName: string;
};

type Props = RouteComponentProps<MatchParams>;

const TopBar: FunctionComponent<Props> = ({
  match: {
    params: { tabName: activeTab },
  },
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { isTeacher, isDataManager, isDistrictOwner } = useUserRole();
  const userRole = useSelector(
    (s: ApplicationState) =>
      s.auth.userInfo?.profile.current_assignment?.role
  );
  const showTopBar = useSelector((s: ApplicationState) => s.help.showTopBar);
  const isParent = userRole === UserAssignmentRole.PARENT;
  const isDistrictRole = userRole ? districtRoles.includes(userRole) : false;

  const activeItem = useMemo(() => {
    // District Admin tabs
    if (isDistrictRole) {
      switch (activeTab) {
        case DistrictOwnerDashboardTabKeys.YOUR_STAFF:
          return (
            <>
              <FontAwesomeIcon icon={faUsers} />
              &nbsp;My Staff
            </>
          );
        case DistrictOwnerDashboardTabKeys.DISTRICT_SUMMARY:
          return (
            <>
              <FontAwesomeIcon icon={faClipboardList} />
              &nbsp;District Summary
            </>
          );
        case DistrictOwnerDashboardTabKeys.MANAGE_SCHOOLS:
          return (
            <>
              <FontAwesomeIcon icon={faBuilding} />
              &nbsp;Manage Schools
            </>
          );
        case DistrictOwnerDashboardTabKeys.MANAGE_DISTRICT_DATA:
          return (
            <>
              <FontAwesomeIcon icon={faTable} />
              &nbsp;Manage District Data
            </>
          );
        case DistrictOwnerDashboardTabKeys.MANAGE_DATA_ACCESS:
          return (
            <>
              <FontAwesomeIcon icon={faUserLock} />
              &nbsp;Manage Data Permissions
            </>
          );
        case DistrictOwnerDashboardTabKeys.ROSTERS_STUDENTS:
          return (
            <>
              <FontAwesomeIcon icon={faRestroom} />
              &nbsp;Student Data
            </>
          );
        case DistrictOwnerDashboardTabKeys.INTERVENTION_LIBRARY:
          return (
            <>
              <FontAwesomeIcon icon={faDatabase} />
              &nbsp;Intervention Library
            </>
          );
        case DistrictOwnerDashboardTabKeys.TUTORIAL_VIDEOS:
          return (
            <>
              <FontAwesomeIcon icon={faVideo} />
              &nbsp;Video Tutorials
            </>
          );
        case DistrictOwnerDashboardTabKeys.RELEASE_NOTES:
          return (
            <>
              <FontAwesomeIcon icon={faStickyNote} />
              &nbsp;Release Notes
            </>
          );
        case DistrictOwnerDashboardTabKeys.YOUR_PROFILE:
          return (
            <>
              <FontAwesomeIcon icon={faUserCheck} />
              &nbsp;My Profile
            </>
          );
        case undefined:
        case "":
        default:
          // Default to My Staff for district roles
          return (
            <>
              <FontAwesomeIcon icon={faUsers} />
              &nbsp;My Staff
            </>
          );
      }
    }
    
    // Coach/Teacher/Parent tabs
    switch (activeTab) {
      case CoachDashboardTabKeys.YOUR_READING_ASSIGNMENTS:
        return (
          <>
            <FontAwesomeIcon icon={faUsers} />
            &nbsp;{isParent ? "Your Children" : "Reading Intervention Groups"}
          </>
        );
      case CoachDashboardTabKeys.YOUR_BEHAVIOUR_ASSIGNMENTS:
        return (
          <>
            <FontAwesomeIcon icon={faUsers} />
            &nbsp;{isParent ? "Your Children" : "Behavior Intervention Groups"}
          </>
        );
      // case CoachDashboardTabKeys.GROUPS_IN_PROGRESS:
      //   return <GroupsInProgressNavLink />;
      case CoachDashboardTabKeys.YOUR_DATA_AND_INTERVIEWS:
        return (
          <>
            <FontAwesomeIcon icon={faTable} />
            &nbsp;My Data
          </>
        );
      case CoachDashboardTabKeys.ROSTERS_STUDENTS:
      case CoachDashboardTabKeys.ROSTERS_TEACHERS:
        return (
          <>
            <FontAwesomeIcon icon={faRestroom} />
            &nbsp; {isTeacher ? `Student Data` : 'Educators & Student Data'}
          </>
        );
      // case CoachDashboardTabKeys.PARENTS:
      //   return (
      //     <>
      //       <FontAwesomeIcon icon={faUniversalAccess} />
      //       &nbsp;Parent/Guardian Access
      //     </>
      //   );
      case CoachDashboardTabKeys.FBAs:
        return (
          <>
            <FontAwesomeIcon icon={faGlasses} />
            &nbsp;ABC Data Collection
          </>
        );
      case CoachDashboardTabKeys.BADGES:
        return (
          <>
            <FontAwesomeIcon icon={faTrophy} />
            &nbsp;Badges
          </>
        );
      case CoachDashboardTabKeys.INTERVENTION_LIBRARY:
        return (
          <>
            <FontAwesomeIcon icon={faDatabase} />
            &nbsp;Intervention Library
          </>
        );
      case CoachDashboardTabKeys.TUTORIAL_VIDEOS:
        return (
          <>
            <FontAwesomeIcon icon={faVideo} />
            &nbsp;Video Tutorials
          </>
        );
      case CoachDashboardTabKeys.RELEASE_NOTES:
        return (
          <>
            <FontAwesomeIcon icon={faStickyNote} />
            &nbsp;Release Notes
          </>
        );
      case CoachDashboardTabKeys.DATA_PERMISSIONS:
        return (
          <>
            <FontAwesomeIcon icon={faShieldAlt} />
            &nbsp;My Data Permissions
          </>
        );
      case CoachDashboardTabKeys.YOUR_PROFILE:
        return (
          <>
            <FontAwesomeIcon icon={faUserCheck} />
            &nbsp;My Profile
          </>
        );

      case CoachDashboardTabKeys.NOTIFICATIONS:
        return (
          <>
            <FontAwesomeIcon icon={faBell} />
            &nbsp;Notifications
          </>
        );

      case CoachDashboardTabKeys.SPELLING_TEST:
        return (
          <>
            <FontAwesomeIcon icon={faSpellCheck} />
            &nbsp;Spelling Test
          </>
        );
      default:
        return <i>Not selected</i>;
    }
  }, [activeTab, isDistrictRole, isParent, isTeacher]);

  const { changeActiveTab } = useActiveTab();

  const handleActiveTabSelect = (activeTab: string | null) => {
    document.body.click(); // causes the popover to close
    
    // Handle district admin navigation separately
    if (isDistrictRole && activeTab) {
      dispatch(push(`/district/${activeTab}`));
      return;
    }
    
    // Handle coach/teacher/parent navigation
    changeActiveTab(activeTab);
  };

  const popover = useMemo(
    () => (
      <Popover id="side-bar-page-popover">
        <Popover.Content>
          <Nav
            bsPrefix="coachDashboardSidebar"
            defaultActiveKey={
              isDistrictRole 
                ? DistrictOwnerDashboardTabKeys.YOUR_STAFF 
                : CoachDashboardTabKeys.YOUR_DATA_AND_INTERVIEWS
            }
            activeKey={activeTab as EventKey}
            onSelect={handleActiveTabSelect}
          >
            {isDistrictRole ? (
              <>
                <h4 className="font-weight-bold pl-3 ml-1">District Management</h4>
                <Nav.Item>
                  <Nav.Link eventKey={DistrictOwnerDashboardTabKeys.YOUR_STAFF}>
                    <FontAwesomeIcon icon={faUsers} />
                    &nbsp;My Staff
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey={DistrictOwnerDashboardTabKeys.DISTRICT_SUMMARY}>
                    <FontAwesomeIcon icon={faClipboardList} />
                    &nbsp;District Summary
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey={DistrictOwnerDashboardTabKeys.MANAGE_SCHOOLS}>
                    <FontAwesomeIcon icon={faBuilding} />
                    &nbsp;Manage Schools
                  </Nav.Link>
                </Nav.Item>
                {(isDataManager || isDistrictOwner) && (
                  <Nav.Item>
                    <Nav.Link eventKey={DistrictOwnerDashboardTabKeys.MANAGE_DISTRICT_DATA}>
                      <FontAwesomeIcon icon={faTable} />
                      &nbsp;Manage District Data
                    </Nav.Link>
                  </Nav.Item>
                )}
                <Nav.Item>
                  <Nav.Link eventKey={DistrictOwnerDashboardTabKeys.MANAGE_DATA_ACCESS}>
                    <FontAwesomeIcon icon={faUserLock} />
                    &nbsp;Manage Data Permissions
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey={DistrictOwnerDashboardTabKeys.ROSTERS_STUDENTS}>
                    <FontAwesomeIcon icon={faRestroom} />
                    &nbsp;Student Data
                  </Nav.Link>
                </Nav.Item>
                
                <h4 className="font-weight-bold pl-3 ml-1" style={{ marginTop: 15 }}>Explore & Learn</h4>
                <Nav.Item>
                  <Nav.Link eventKey={DistrictOwnerDashboardTabKeys.INTERVENTION_LIBRARY}>
                    <FontAwesomeIcon icon={faDatabase} />
                    &nbsp;Intervention Library
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey={DistrictOwnerDashboardTabKeys.TUTORIAL_VIDEOS}>
                    <FontAwesomeIcon icon={faVideo} />
                    &nbsp;
                    {intl.formatMessage({
                      id: "app.navigation.dashboardMenuPanel.videoTutorialLabel",
                    })}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey={DistrictOwnerDashboardTabKeys.RELEASE_NOTES}>
                    <FontAwesomeIcon icon={faStickyNote} />
                    &nbsp;Release Notes
                  </Nav.Link>
                </Nav.Item>
                <HelpButton />
                
                <h4 className="font-weight-bold pl-3 ml-1" style={{ marginTop: 15 }}>Settings</h4>
                <Nav.Item>
                  <Nav.Link eventKey={DistrictOwnerDashboardTabKeys.YOUR_PROFILE}>
                    <FontAwesomeIcon icon={faUserCheck} />
                    &nbsp;My Profile
                  </Nav.Link>
                </Nav.Item>
                <SettingButton />
                <div className="d-flex justify-content-center mt-2">
                  <button
                    id="changeMenuBarPositionBtn"
                    className="blueBtnSm"
                    style={{ width: "100%" }}
                    onClick={() => {
                      dispatch(changeDisplayedBar());
                      // Close the popover when showing sidebar
                      if (showTopBar) {
                        document.body.click();
                      }
                    }}
                  >
                    {intl.formatMessage({
                      id: showTopBar
                        ? "app.navigation.dashboardMenuPanel.openSidebarMenuLabel"
                        : "app.navigation.dashboardMenuPanel.hideSidebarMenuLabel",
                    })}
                  </button>
                </div>
              </>
            ) : (
              <DashboardMenuPanel activeTab={activeTab} />
            )}
          </Nav>
        </Popover.Content>
      </Popover>
    ),
    [activeTab, isDistrictRole, isDataManager, isDistrictOwner, intl, showTopBar]
  );

  return (
    <div className="topBar" id="topBar" style={{ display: showTopBar ? "flex" : "none" }}>
      <OverlayTrigger
        trigger="click"
        rootClose
        overlay={popover}
        placement="bottom"
      >
        <div>
          <h4 className="text-center font-weight-bold">Main Menu</h4>
          <div className="pageBtn">
            <span className="flex-grow-1">{activeItem}</span>
            <span>
              <FontAwesomeIcon icon={faBars} size="lg" />
            </span>
          </div>
        </div>
      </OverlayTrigger>
    </div>
  );
};

export default withRouter(TopBar);
